import React, { Fragment, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// Components

import Logo from '@common-components/headers/fragments/Logo';
import Header from '@common-components/headers/header';
import TopNavHeader from '@common-components/headers/TopNavHeader';
import Impersonation from '../individualPortal/Impersonation';

// ActionCreators

import { changeLang, showModal } from '@actions/common/actionCreators';
import { fetchUnreadNotificationCount } from '@actions/individualPortal/secureInbox/secureInboxActionCreators';

// Utils

import { isFormInInitialState } from '@reducers/appReducer';

// Hooks

import { useInboxId } from '@hooks/useInboxId';
import { useTitle } from '@hooks/useTitle';

// Constants
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { allowedRoutes } from '@constants/individualPortal/capAlerts/capAlerts.constants';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import CapAlertContainer from '@containers/individualPortal/capAlerts/CapAlertContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@hooks/useLocation';

const HeaderContainer = props => {
    const {
        actions: { changeLang, fetchUnreadNotificationCount, showModal },
        adjustedFlow,
        environment,
        environment: {
            exchangeName,
            capAlertsVisible,
            language,
            stateCode,
            impersonation,
            impersonationDetails,
            assets: { LOGO: logo, LOGO_MOBILE: logoMobile },
            links: { LOGO_URL: logoUrlLink, DASHBOARD: dashboardUrlLink },
            telexEnabled,
            unreadMessageCount,
            user = {},
        },
        error: { errorStatus },
        messages: { loadedLanguages },
        page,
        ssapForm,
    } = props;

    const { activeModuleId: householdId = 0, role, roles, userId } = user || {};

    const { name, logoUrl: carrierLogoUrl, logoUrlMobile, micrositeIds } = environment.affiliate || {};

    const currentPath = window.location.pathname;

    const headerProps = {
        environment,
        language,
        changeLang,
        showModal,
        currentPath,
        errorStatus,
        ssapForm,
    };

    const isShowTopNav = roles && Object.keys(roles).length > 1 && role != ALL_USER_ROLES.INDIVIDUAL;
    const topNavHeaderProps = { environment, hasSubMenu: false, isTopNav: true, stateCode: environment.stateCode };

    const { currentLocation } = useLocation();

    useTitle('', {
        adjustedFlow,
        exchangeName,
        language,
        loadedLanguages,
        page,
        pathname: currentLocation,
        stateCode,
    });
    const inboxIdData = {
        householdId: householdId,
        impersonation,
        impersonationDetails,
        telexEnabled,
        role,
        userId,
    };

    const { part, inboxId } = useInboxId(inboxIdData);

    useEffect(() => {
        telexEnabled && user && fetchUnreadNotificationCount(part, inboxId);
    }, [unreadMessageCount]);

    const isCapAlertVisible = useMemo(() => {
        return capAlertsVisible && allowedRoutes.some(route => window.location.pathname.includes(route));
    }, [window.location.pathname]);

    return (
        <Fragment>
            <a className="usa-skipnav gi-skipnav" href="#main-content">
                Skip to Main Content
            </a>
            {isShowTopNav ? <TopNavHeader {...topNavHeaderProps} /> : null}

            <div className="main-nav-masthead main-nav-submenu">
                <header className="usa-header usa-header--basic gi-masthead" id={`masthead_${stateCode}`} role="banner">
                    <div className="usa-nav-container gi-masthead__container">
                        <div className="usa-navbar gi-navbar">
                            <Logo
                                {...{
                                    logo,
                                    logoUrlLink,
                                    dashboardUrlLink,
                                    logoMobile,
                                    stateCode,
                                    name,
                                    carrierLogoUrl,
                                    logoUrlMobile,
                                    micrositeIds,
                                    hasUser: !!environment.user,
                                    isAnonymous: environment.anonymous,
                                }}
                            />
                        </div>
                        <nav role="navigation" className="usa-nav gi-main__nav">
                            <button aria-label="Close" className="usa-nav__close gi-main__nav__close">
                                <FontAwesomeIcon icon="times" />
                            </button>
                            <Header {...headerProps} />
                        </nav>
                    </div>
                </header>
                <Impersonation {...{ impersonation, impersonationDetails, changeLang }} />
                <ConditionalRendering isTrue={isCapAlertVisible}>
                    <CapAlertContainer />
                </ConditionalRendering>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    messages: state.common.messages,
    error: state.common.error,
    adjustedFlow: state.ssap.adjustedFlow,
    page: state.ssap.page,
    ssapForm: isFormInInitialState(state),
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            changeLang,
            fetchUnreadNotificationCount,
            showModal,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(HeaderContainer);
